import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import RouteModal from "../components/RouteModal";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.995); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const shake = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
  100% { transform: translateX(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg,#fff7ed,#fff9f2);
`;

const Card = styled.div`
  background: linear-gradient(180deg, #ffffff, #fffdf9);
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(9,30,66,0.08);
  padding: 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: transform 150ms ease, box-shadow 150ms ease;
  animation: ${fadeUp} 420ms cubic-bezier(.2,.9,.2,1) both;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 40px rgba(249,115,22,0.12);
  }
`;

const UserInfo = styled.div`
  background: linear-gradient(180deg,#fffdf9,#fff7ed);
  padding: 1.5rem;
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(9,30,66,0.06);
  margin-bottom: 1rem;
  border: 1px solid rgba(249,115,22,0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

const UserFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const UserField = styled.div`
  font-size: 15px;
  color: #0f172a;
  strong {
    color: #f97316;
  }
`;

const Title = styled.h2`
  margin-bottom: 1rem;
  color: #1f2937;
  letter-spacing: -0.2px;
`;

const EntryList = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit,minmax(280px,1fr));
`;

const Error = styled.div`
  color: #7f1d1d;
  background: #fee2e2;
  padding: 10px 12px;
  border-radius: 10px;
  font-weight: 600;
  animation: ${shake} 420ms ease;
  margin-bottom: 1rem;
`;

const Button = styled.button`
  padding: 10px 16px;
  background: linear-gradient(90deg,#ff7a2a 0%, #ffb06b 100%);
  color: #fff;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  transition: transform 140ms ease, box-shadow 140ms ease;
  box-shadow: 0 8px 20px rgba(249,115,22,0.12);
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 28px rgba(249,115,22,0.14);
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 6px 18px rgba(249,115,22,0.12);
  }
`;

const SingleUser = () => {
  const { id } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [err, setErr] = useState("");
  const [showRouteModal, setShowRouteModal] = useState(false);

useEffect(() => {
  const fetchUser = async () => {
    try {
      const res = await api.get(`/api/dataanalysis/surveyors/${id}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });

      // THIS IS THE ONLY CHANGE NEEDED
      setUser(res.data.data.surveyor);     // ← was .user → now .surveyor
      setEntries(res.data.data.entries || []);

    } catch (error) {
      console.error(error);
      setErr(error.response?.data?.message || "Error fetching surveyor data");
    }
  };
  fetchUser();
}, [id, auth.token]);

  if (err) return <Page><Error>{err}</Error></Page>;
  if (!user) return <Page>Loading...</Page>;

  return (
    <Page>
      <UserInfo>
        <UserFields>
          <Title>User Details</Title>
          <UserField><strong>Name:</strong> {user.name}</UserField>
          <UserField><strong>Email:</strong> {user.email}</UserField>
          <UserField><strong>Role:</strong> {user.role || "N/A"}</UserField>
          <UserField><strong>Created At:</strong> {new Date(user.createdAt).toLocaleString()}</UserField>
        </UserFields>
        <Button onClick={() => setShowRouteModal(true)}>Define Route</Button>
      </UserInfo>

      <Title>Entries</Title>
      {entries.length === 0 ? (
        <Error>No entries found for this user.</Error>
      ) : (
        <EntryList>
          {entries.map((e) => (
            <Card key={e._id} onClick={() => navigate(`/entries/${e._id}`)}>
              <div><strong>Dog Name:</strong> {e.fields?.dogName || "N/A"}</div>
              <div><strong>Breed:</strong> {e.fields?.breed || "N/A"}</div>
              <div><strong>Date:</strong> {new Date(e.createdAt).toLocaleDateString()}</div>
            </Card>
          ))}
        </EntryList>
      )}

      {showRouteModal && (
        <RouteModal
          isOpen={showRouteModal}
          onClose={() => setShowRouteModal(false)}
          onSave={() => setShowRouteModal(false)}
          userId={user._id}     // preselect user in modal
          userName={user.name}  // display name in dropdown
        />
      )}
    </Page>
  );
};

export default SingleUser;
